<template>
  <v-card>
    <v-card-title class="pb-0 mb-0">
      <div class="card-title">
        <span>{{ title }}</span>
        <div class="d-flex align-center">
          <export-data :data-fetcher="getDataToExport" filename="resultados_gerais_industria" ghost />

          <headers-data-table-settings
            key="industry-general-results"
            :headers.sync="headers"
            :default-headers="getDefaultHeadersOptions()"
            headers-settings-name="industry-general-results"
          />
          <div class="min-width-345">
            <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Busca" />
          </div>
        </div>
      </div>
    </v-card-title>
    <v-card-text class="pa-0">
      <paginated-data-table
        ref="table"
        :headers="computedHeaders"
        :search="search"
        :query="query"
        :query-name="queryName"
        :skip="skipTableQuery"
        query-main-property-name="offerSchedules"
        :graphql-uri="$microservicesUrls['sales']"
        :query-variables="{ ...queryVariables, franchise }"
        :custom-items-per-page="20"
        :items-per-page-options="[10, 15, 20]"
        reset-page-when-query-variable-change
      >
        <template v-slot:item.status="{ item }">
          <div class="d-flex align-center">
            <div class="fix-td-position">
              <mf-icon v-if="getStatus(item.status).color" fill size="16" :color="getStatus(item.status).color" icon="fiber_manual_record" />
              {{ getStatus(item.status).text }}
            </div>
          </div>
        </template>

        <template v-slot:item.name="{ item }" style="max-width: 300px">
          <div class="fix-td-position">
            <tooltip :message="item.name" align="top">
              <router-link v-if="!isFranchisee" class="link" :to="getResultsRoute(item)">
                {{ item.name | truncate({ length: 62 }) }}
              </router-link>
              <router-link
                v-else-if="isFranchisee && (!item.industry || allowsViewingFranchiseTradeResults)"
                class="link"
                :to="getResultsRoute(item, isFranchisee)"
              >
                {{ item.name | truncate({ length: 62 }) }}
              </router-link>
              <span v-else>{{ item.name | truncate({ length: 62 }) }}</span>
            </tooltip>
          </div>
        </template>

        <template v-slot:item.date="{ item }">
          <div class="fix-td-position">
            {{ DateTime.fromISO(item.start_date).toFormat('dd/MM/yyyy') }} até {{ DateTime.fromISO(item.end_date).toFormat('dd/MM/yyyy') }}
          </div>
        </template>

        <template v-slot:item.modality="{ item }">
          <div class="fix-td-position">
            {{ getModality(item).text }}
          </div>
        </template>

        <template v-slot:item.types="{ item }">
          <div class="fix-td-position">
            <tooltip :message="getOfferTypesTooltip(item.types)" align="top" :disabled="getOfferTypesTooltip(item.types).length <= 1">
              <span>{{ getOfferTypes(item.types).length === 1 ? getOfferTypes(item.types)[0] : 'Múltiplos' }}</span>
            </tooltip>
          </div>
        </template>

        <template v-slot:item.industry_name="{ item }">
          <div class="fix-td-position">
            {{ item.industry.name }}
          </div>
        </template>

        <template v-slot:item.is_personalized="{ item }">
          <div class="fix-td-position">
            {{ item.industry.is_mercapromo ? 'Massivo' : item.segments.length ? 'Personalizado' : 'Clube' }}
          </div>
        </template>

        <template v-slot:item.rebate="{ item }">
          <div class="fix-td-position">
            <span v-if="rebateValue(item)">
              {{ locale(rebateValue(item), 2) | localeString(2) | currency }}
            </span>
            <span v-else> - </span>
          </div>
        </template>
      </paginated-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { DateTime } from 'luxon'
import cloneDeep from 'lodash/cloneDeep'
import { localeString } from '@/config/helpers.js'
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import ExportData from '@/components/export-data/ExportData.vue'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import Tooltip from '@/modules/campanhas/gestao-ofertas-v2/views/components/Tooltip.vue'
import { QUERY_GENERAL_RESULTS } from '../../graphql'
import getModality from '@/modules/industria/helpers/modalityHelper.js'

export default {
  components: {
    PaginatedDataTable,
    HeadersDataTableSettings,
    ExportData,
    Tooltip,
  },
  props: {
    title: {
      type: String,
      default: 'Resultados por oferta da rede',
    },
    queryVariables: {
      type: Object,
      default: () => ({}),
    },
    franchise: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    DateTime: DateTime,
    locale: localeString,
    headers: [],
    search: '',
  }),
  computed: {
    defaultHeaders() {
      return [
        {
          text: 'Oferta',
          value: 'name',
          width: '25%',
          isActive: true,
          isRequired: true,
          isDefault: true,
        },
        {
          text: 'Período campanha',
          value: 'date',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Modalidade',
          value: 'modality',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Formato',
          value: 'types',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Público-alvo',
          value: 'is_personalized',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Indústria',
          value: 'industry_name',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Reembolso',
          value: 'rebate',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Apuração do reembolso',
          value: 'status',
          width: '10%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
      ]
    },

    computedHeaders() {
      return this.headers.filter((header) => header.isActive)
    },

    query() {
      return QUERY_GENERAL_RESULTS
    },

    queryName() {
      return 'offersGeneralResultsIndustry'
    },

    skipTableQuery() {
      return !this.queryVariables?.startDate || !this.queryVariables?.endDate
    },

    isFranchisee() {
      if (!this.$getLoggedUser().is_franchisee) return false
      return this.$getLoggedUser().is_franchisee
    },

    allowsViewingFranchiseTradeResults() {
      if (this.$permissions.havePagePermission('campanhas/gestao-ofertas/trade/resultados_franquia')) {
        return true
      }
      return false
    },
  },
  methods: {
    getModality,
    getResultsRoute(item) {
      let routeName = item.industry.is_mercapromo ? 'ofertas-trade-resultado-mercapromo' : 'ofertas-trade-resultado'
      return { name: routeName, params: { id: item._id }, query: { franchise: this.isFranchisee } }
    },

    getStatus(status) {
      switch (status) {
        case 'counting':
          return {
            text: 'Em apuração',
            color: '#BFE600',
          }
        case 'counted':
          return {
            text: 'Apuração concluída',
            color: '#0A8533',
          }
        default:
          return {
            text: '-',
            color: '',
          }
      }
    },

    getOfferTypes(types) {
      const offerTypes = []
      types.forEach((type) => offerTypes.push(getProductFormatTypeLabel(type)))
      return offerTypes
    },

    getOfferTypesTooltip(types) {
      return this.getOfferTypes(types).join(', ')
    },

    isZeDelivery(types) {
      return types.includes('benefits')
    },

    rebateValue(item) {
      if (this.isZeDelivery(item.types)) {
        return item.month_rebate
      } else {
        return item.rebate
      }
    },

    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },

    getDataToExport() {
      const headers = this.computedHeaders.map((header) => header.text)
      const rows = this.$refs.table.items.map((item) =>
        this.computedHeaders.map((header) => {
          switch (header.value) {
            case 'date':
              return `${DateTime.fromISO(item.start_date).toFormat('dd/MM/yyyy')} até ${DateTime.fromISO(item.end_date).toFormat('dd/MM/yyyy')}`
            case 'types':
              return this.getOfferTypes(item.types)[0]
            case 'modality':
              return this.getModality(item).text
            case 'is_personalized':
              return item.industry.is_mercapromo ? 'Massivo' : item.segments.length ? 'Personalizado' : 'Clube'
            case 'industry_name':
              return item.industry.name
            case 'status':
              return this.getStatus(item.status).text
            default:
              return item[header.value]
          }
        })
      )
      return { headers, rows }
    },
  },
}
</script>

<style lang="scss" scoped>
.card-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 0;
  padding-bottom: 16px;
}

.min-width-345 {
  min-width: 345px;
}

.fix-td-position {
  padding: 0 16px;
  display: flex;
  align-items: center;
}

::v-deep tr:nth-child(even) {
  background-color: #f7f7f7;
}

::v-deep td {
  padding: 0 !important;
}
</style>
